import { Injectable, inject, computed, signal } from "@angular/core";
import { IFirmRolesService, MappingOfStatusesService, UnassignedUserId, UsersService, compareByFirstAndLastNames } from "@shared/index";
import { ITranslateObject, TaxprepT1Card } from "../views-interfaces";
import { MultiSelectModel, Option } from "./filter-types";
import { CardsService } from "@services/index";

export class AssigneeFilterValue {
    userId: string;
    roleId: string | null;
}

@Injectable({ providedIn: "root" })
export class AssigneesFilterGeneratorService {
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly usersService = inject(UsersService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly cardsService = inject(CardsService);

    private readonly selection = signal<object>({});

    public readonly models = computed<MultiSelectModel[]>(
        () => {
            const selection = this.selection();
            const usersById = this.usersService.usersById();
            // Signal needs to react to changes in users (to split)
            this.cardsService.cardsByUsers();

            const models: MultiSelectModel[] = [];

            const users = Array.from(usersById?.fromId?.values() ?? []);

            // First model for user assigned to any role
            const usersOptions: Option[] = [
                {
                    text: "FiltersSidebar.Options.Users.Unassigned",
                    value: JSON.stringify({ userId: null, roleId: null }),
                },
            ].concat(
                users
                    .sort((a, b) => {
                        const byActive = Number(b.Active) - Number(a.Active);
                        if (byActive !== 0) return byActive;

                        return compareByFirstAndLastNames(a, b);
                    })
                    .map((user) => {
                        return {
                            text: user.FullName,
                            value: JSON.stringify({ userId: user.Id, roleId: null }),
                            isVisible: user.Active || this.cardsService.getCardsCountForUser(user).total > 0,
                        };
                    }),
            );
            const applicableUserOptions = usersOptions.filter(
                (option) => !("isVisible" in option) || (typeof option.isVisible === "boolean" ? option.isVisible : option?.isVisible?.(undefined)),
            );
            const usersSelection = selection ? (selection["users"] ?? []) : [];
            models.push({
                label: "FiltersSidebar.Labels.Users",
                id: "users",
                allOptions: usersOptions,
                applicableOptions: applicableUserOptions,
                selection: usersSelection.filter(
                    (selected) => selected && applicableUserOptions.some((option) => option && option?.value === selected?.value),
                ),
            } as MultiSelectModel);

            // Then, for user by role
            const usersByRoles = this.iFirmRolesService.rolesWithUsers();
            // We have an issue where getRolesList is not called in wrapper, which would wipe roles assigned.
            this.iFirmRolesService.iFirmRoles
                .slice()
                .sort((a, b) => {
                    if (Number.parseInt(a.Id, 10) < Number.parseInt(b.Id, 10)) return -1;
                    if (Number.parseInt(a.Id, 10) > Number.parseInt(b.Id, 10)) return 1;
                    return 0;
                })
                .forEach((role) => {
                    const options: Option[] = [
                        {
                            text: "FiltersSidebar.Options.Users.Unassigned",
                            value: JSON.stringify({ userId: null, roleId: role.Id }),
                        },
                    ].concat(
                        Array.from(usersByRoles.get(role.Id)?.Users?.values() ?? [])
                            .sort((a, b) => {
                                const byActive = Number(b.Active) - Number(a.Active);
                                if (byActive !== 0) return byActive;

                                return compareByFirstAndLastNames(a, b);
                            })
                            .map((entry) => {
                                return {
                                    text: entry.FullName,
                                    value: JSON.stringify({ userId: entry.Id, roleId: role.Id }),
                                    isVisible: entry.Active || (this.cardsService.getCardsCountForUser(entry).totalByRole?.[role.Id] ?? 0) > 0,
                                };
                            }),
                    );
                    const applicableuserRoleOptions = options.filter(
                        (option) =>
                            !("isVisible" in option) || (typeof option.isVisible === "boolean" ? option.isVisible : option?.isVisible?.(undefined)),
                    );
                    const userRoleselection = selection ? (selection[`role-${role.Id}`] ?? []) : [];
                    models.push({
                        id: `role-${role.Id}`,
                        label: role.Description,
                        allOptions: options,
                        applicableOptions: applicableuserRoleOptions,
                        selection: userRoleselection.filter(
                            (selected) => selected && applicableuserRoleOptions.some((option) => option && option?.value === selected?.value),
                        ),
                    } as MultiSelectModel);
                });
            return models;
        },
        { equal: (a, b) => this.modelsEqual(a, b) },
    );

    private modelsEqual(a: MultiSelectModel[], b: MultiSelectModel[]): boolean {
        if (a === b) return true;
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i].id !== b[i].id) return false;
            if (!this.optionsEqual(a[i].applicableOptions, b[i].applicableOptions)) return false;
            if (!this.optionsEqual(a[i].selection, b[i].selection)) return false;
        }
        return true;
    }

    private optionsEqual(a: Option[], b: Option[]): boolean {
        if (a === b) return true;
        const aLen = a?.length ?? 0;
        const bLen = b?.length ?? 0;
        if (aLen !== bLen) return false;
        for (let i = 0; i < aLen; i++) {
            if (a[i]?.value !== b[i]?.value || a[i]?.text !== b[i]?.text) return false;
        }
        return true;
    }

    public resetCache() {
        this.selection.set({});
    }

    public loadModels(selection: object) {
        if (!selection) return;
        this.selection.set(selection);
    }

    public updateSelection(modelId: number, selection: Option[]): MultiSelectModel | undefined {
        const selectionType = this.models()[modelId]?.id;
        if (selectionType) {
            this.selection.update((prev) => {
                const next = { ...prev };
                next[selectionType] = selection;
                return next;
            });
        }
        return this.models()[modelId];
    }

    public updateModel(modelId: string, selection: Option[]): MultiSelectModel | undefined {
        const modelIndex = this.models().findIndex((model) => model.id === modelId);
        if (modelIndex !== -1) {
            return this.updateSelection(modelIndex, selection);
        }
    }

    public getPrintDescription(
        label: string | ITranslateObject,
        selectedValues: string[],
    ): { label: string | ITranslateObject; values: (string | ITranslateObject)[] } | null {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        }

        const values: (string | ITranslateObject)[] = selectedValues.map((value) => {
            const user = this.usersService.getUserById(JSON.parse(value)?.userId);
            return user
                ? ({
                      "en-CA": `${user.FirstName} ${user.LastName}`,
                      "fr-CA": `${user.FirstName} ${user.LastName}`,
                  } as ITranslateObject)
                : "FiltersSidebar.Options.Users.Unassigned";
        });

        return { label, values };
    }

    public generateFilter(selectedValues: string[], cardsReadyToBeWorkedOn: boolean) {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        } else {
            const selection = selectedValues.map((value) => JSON.parse(value) as AssigneeFilterValue);
            if (cardsReadyToBeWorkedOn) {
                return (card: TaxprepT1Card) => {
                    const assignedRoleId = this.mappingOfStatusesService.getStatusFromStatusId(card.return.statusId)?.RoleId;
                    return selection.some((value) => {
                        if (value.roleId === null) {
                            return card.roleUserMapping.some(
                                (mapping) => mapping.roleId === assignedRoleId && this.userIdMatch(mapping.userId, value.userId),
                            );
                        } else {
                            const found = card.roleUserMapping.find(
                                (mapping) => mapping.roleId === assignedRoleId && mapping.roleId === value.roleId,
                            );
                            return (!found && value.userId === null) || (found && this.userIdMatch(found.userId, value.userId));
                        }
                    });
                };
            } else {
                return (card: TaxprepT1Card) => {
                    return selection.some((value) => {
                        if (value.roleId === null) {
                            return card.roleUserMapping.some((mapping) => {
                                return this.userIdMatch(mapping.userId, value.userId);
                            });
                        } else {
                            const found = card.roleUserMapping.find((mapping) => {
                                return mapping.roleId === value.roleId;
                            });
                            return (!found && value.userId === null) || (found && this.userIdMatch(found.userId, value.userId));
                        }
                    });
                };
            }
        }
    }

    private userIdMatch(mappingUserId: string, expected: string) {
        const mappingUser = this.usersService.getUserById(mappingUserId);
        return (
            mappingUser?.Id === expected ||
            (expected === null &&
                (mappingUserId === null || mappingUserId === undefined || mappingUserId === "" || mappingUserId === UnassignedUserId))
        );
    }
}
